// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React from "react";
import {ITag} from "../../../../../models/applicationState";
import "./predictResult.scss";
import {getPrimaryGreenTheme} from "../../../../../common/themes";
import {PrimaryButton, ContextualMenu, IContextualMenuProps, IIconProps} from "@fluentui/react";
import {strings} from "../../../../../common/strings";
import {tagIndexKeys} from "../../../common/tagInput/tagIndexKeys";
import {downloadFile, downloadZipFile, zipData} from "../../../../../common/utils";

export interface IAnalyzeModelInfo {
    docType: string,
    modelId: string,
    docTypeConfidence: number,
}

export interface IPredictResultProps {
    predictions: {[key: string]: any};
    analyzeResult: {};
    downloadPrefix?: string;
    page: number;
    tags: ITag[];
    downloadResultLabel: string;
    onAddAssetToProject?: () => void;
    onPredictionClick?: (item: any) => void;
    onPredictionMouseEnter?: (item: any) => void;
    onPredictionMouseLeave?: (item: any) => void;
}

export interface IPredictResultState { }

export default class PredictResult extends React.Component<IPredictResultProps, IPredictResultState> {
    public render() {
        const {tags, predictions} = this.props;
        const tagsDisplayOrder = tags.map((tag) => tag.name);
        for (const name of Object.keys(predictions)) {
            const prediction = predictions[name];
            if (prediction != null) {
                prediction.fieldName = name;
                prediction.displayOrder = tagsDisplayOrder.indexOf(name);
            }
        }
        // not sure if we decide to filter item by the page
        const items = Object.values(predictions).filter(Boolean).sort((p1, p2) => p1.displayOrder - p2.displayOrder);
        const menuProps: IContextualMenuProps = {
            className: "keep-button-120px",
            items: [
                {
                    key: 'JSON',
                    text: 'JSON',
                    onClick: () => this.triggerJSONDownload()
                },
                {
                    key: 'CSV',
                    text: 'CSV',
                    onClick: () => this.triggerCSVDownload()
                }
            ]
        }
        return (
            <div>
                <div className="container-items-center container-space-between results-container">
                    <h5 className="results-header">Prediction results</h5>

                </div>
                <div className="container-items-center container-space-between">
                    {this.props.onAddAssetToProject ?
                        <PrimaryButton
                            theme={getPrimaryGreenTheme()}
                            onClick={this.onAddAssetToProject}
                            text={strings.predict.editAndUploadToTrainingSet} />
                        :<span></span>
                    }
                    <PrimaryButton
                        className="align-self-end keep-button-120px"
                        theme={getPrimaryGreenTheme()}
                        text="Download"
                        allowDisabledFocus
                        autoFocus={true}
                        menuProps={menuProps}
                        menuAs={this.getMenu}
                    />
                </div>
                {this.props.children}
                <div className="prediction-field-header" style={{marginTop: 28}}>
                    <h6 className="prediction-field-header-field"> Page # / Field name / Value</h6>
                    <h6 className="prediction-field-header-confidence"> Confidence</h6>
                </div>
                <div className="prediction-header-clear"></div>

                {items.map((item: any, key) => this.renderItem(item, key))}
            </div>
        );
    }

    private getMenu(props: IContextualMenuProps): JSX.Element {
        return <ContextualMenu {...props} />;
    }

    private renderItem = (item: any, key: any) => {
        const postProcessedValue = this.getPostProcessedValue(item);
        const style: any = {
            marginLeft: "0px",
            marginRight: "0px",
            background: this.getTagColor(item.fieldName),
        };
        return (
            <div key={key}
                onClick={() => this.onPredictionClick(item)}
                onMouseEnter={() => this.onPredictionMouseEnter(item)}
                onMouseLeave={() => this.onPredictionMouseLeave(item)}>
                <li className="predictiontag-item" style={style}>
                    <div className={"predictiontag-color"}>
                        <span>{item.page}</span>
                    </div>
                    <div className={"predictiontag-content"}>
                        {this.getPredictionTagContent(item)}
                    </div>
                </li>
                {item.text === null ?
                    <>
                        <li className={postProcessedValue ? "predictiontag-item-label-null mt-0" : "predictiontag-item-label-null mt-0 mb-1"}>
                            {postProcessedValue ? "text: NULL": "NULL"}
                        </li>
                    </>
                    :
                    <>
                        <li className={postProcessedValue ? "predictiontag-item-label mt-0" : "predictiontag-item-label mt-0 mb-1"}>
                            {postProcessedValue ? "text: " + item.text : item.text}
                        </li>
                        {postProcessedValue &&
                            <li className="predictiontag-item-label mb-1">
                                {postProcessedValue}
                            </li>
                        }
                    </>
                }
            </div>
        );
    }

    private getTagColor = (name: string): string => {
        const tag: ITag = this.props.tags.find((tag) => tag.name.toLocaleLowerCase() === name.toLocaleLowerCase());
        if (tag) {
            return tag.color;
        }
        return "#999999";
    }

    private getPredictionTagContent = (item: any) => {
        return (
            <div className={"predictiontag-name-container"}>
                <div className="predictiontag-name-body">
                    {
                        <span title={item.fieldName} className="predictiontag-name-text px-2">
                            {item.fieldName}
                        </span>
                    }
                </div>
                <div className={"predictiontag-confidence"}>
                    {isNaN(item.confidence) ? <span>NaN</span> :
                        <span>{(item.confidence * 100).toFixed(2) + "%"}</span>}
                </div>
            </div>
        );
    }

    private onAddAssetToProject = async () => {
        if (this.props.onAddAssetToProject) {
            this.props.onAddAssetToProject();
        }
    }

    private triggerJSONDownload = (): void => {
        const {analyzeResult} = this.props;
        const predictionData = JSON.stringify(analyzeResult);
        downloadFile(predictionData, this.props.downloadResultLabel + ".json", this.props.downloadPrefix);
    }

    private triggerCSVDownload = (): void => {
        const data: zipData[] = [];
        const items = this.getItems();
        let csvContent: string = `Key,Value,Confidence,Page,Bounding Box`;
        items.forEach(item => {
            csvContent += `\n"${item.fieldName}","${item.text ?? ""}",${isNaN(item.confidence)? "NaN":(item.confidence * 100).toFixed(2) + "%"},${item.page},"[${item.boundingBox}]"`;
        });
        data.push({
            fileName: `${this.props.downloadPrefix}${this.props.downloadResultLabel}-keyvalues.csv`,
            data: csvContent
        });

        let tableContent: string = "";
        const itemNames=["fieldName","text","confidence","page","boundingBox"];
        const getValue=(item:any, fieldName:string)=>{
            switch(fieldName){
                case "fieldName":
                    return `"${item[fieldName]}"`;
                case "text":
                    return `"${item[fieldName]}"`;
                case "confidence":
                    return isNaN(item.confidence)? "NaN":(item.confidence * 100).toFixed(2) + "%";
                case "page":
                    return item[fieldName];
                case "boundingBox":
                    return `"[${item.boundingBox}]"`;
                default:
                    return "";
            }
        }
        itemNames.forEach(name=>{
            tableContent+=(name+",");
            items.forEach(item=>{
                tableContent+=(getValue(item,name)+",");
            })
            tableContent+="\n";
        })
        data.push({
            fileName: `${this.props.downloadPrefix}${this.props.downloadResultLabel}-table.csv`,
            data: tableContent
        });
        downloadZipFile(data, this.props.downloadResultLabel);
    }

    private getItems() {
        const {tags, predictions} = this.props;
        const tagsDisplayOrder = tags.map((tag) => tag.name);
        for (const name of Object.keys(predictions)) {
            const prediction = predictions[name];
            if (prediction != null) {
                prediction.fieldName = name;
                prediction.displayOrder = tagsDisplayOrder.indexOf(name);
            }
        }
        // not sure if we decide to filter item by the page
        const items = Object.values(predictions).filter(Boolean).sort((p1, p2) => p1.displayOrder - p2.displayOrder);
        return items;
    }

    private toPercentage = (x: number): string => {
        return (100 * x).toFixed(1) + "%";
    }

    private onPredictionClick = (prediction: any) => {
        if (this.props.onPredictionClick) {
            this.props.onPredictionClick(prediction);
        }
    }

    private onPredictionMouseEnter = (prediction: any) => {
        if (this.props.onPredictionMouseEnter) {
            this.props.onPredictionMouseEnter(prediction);
        }
    }

    private onPredictionMouseLeave = (prediction: any) => {
        if (this.props.onPredictionMouseLeave) {
            this.props.onPredictionMouseLeave(prediction);
        }
    }

    private getPostProcessedValue = (prediction: any) => {
        if (!prediction.type || !prediction.text) {
            return null;
        }
        const predictionType = prediction.type;
        const predictionText = prediction.text;
        let postProcessedValue;
        let valueType;
        switch (predictionType) {
            case "string":
                valueType = "valueString";
                postProcessedValue = prediction.valueString;
                break;
            case "date":
                valueType = "valueDate";
                postProcessedValue = prediction.valueDate;
                break;
            case "number":
                valueType = "valueNumber";
                postProcessedValue = prediction.valueNumber?.toString();
                break;
            case "integer":
                valueType = "valueInteger";
                postProcessedValue = prediction.valueInteger?.toString();
                break;
            case "time":
                valueType = "valueTime";
                postProcessedValue = prediction.valueTime;
                break;
            default:
                return null;
        }
        if (typeof postProcessedValue === "string" && predictionText !== postProcessedValue) {
            return valueType + ": " + postProcessedValue;
        } else {
            return null;
        }
    }
}
